import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../hooks/useAuth';
import { useSessionTimeout } from '../hooks/useSessionTimeout';
import { Helmet } from 'react-helmet-async';
import AuthGuard from '../components/AuthGuard';
import AdminNavigationShed from '../components/AdminNavigationShed';
import AdminDashboardShed from '../components/admin/AdminDashboardShed';
import DealerDashboard from '../components/admin/DealerDashboard';
import MasterDealerManager from '../components/admin/MasterDealerManager';
import EmailStatusChecker from '../components/admin/EmailStatusChecker';
import ShedManager from '../components/admin/ShedManager';
import LeadManagement from '../components/admin/LeadManagement';
import PaymentManagement from '../components/admin/PaymentManagement';
import AdminSettings from '../components/admin/AdminSettings';
import AdminAnalytics from '../components/admin/AdminAnalytics';
import DealerColorManager from '../components/admin/DealerColorManager';
import DealerSizeManager from '../components/admin/DealerSizeManager';
import DealerWindowSizeManager from '../components/admin/DealerWindowSizeManager';

const Admin = () => {
  const { user, userRole, dealerId } = useAuth();
  const isDealer = userRole === 'dealer_admin' || userRole === 'dealer_staff';
  const [activeTab, setActiveTab] = useState(isDealer ? 'dealer-dashboard' : 'dashboard');
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  
  // Session timeout with 30 minute timeout, 5 minute warning
  useSessionTimeout(30, 5);

  // Update default tab when role loads
  useEffect(() => {
    if (userRole === 'dealer_admin' || userRole === 'dealer_staff') {
      setActiveTab(prev => prev === 'dashboard' ? 'dealer-dashboard' : prev);
    }
  }, [userRole]);

  const renderContent = () => {
    switch (activeTab) {
      case 'dashboard':
        return isDealer ? <DealerDashboard setActiveTab={setActiveTab} /> : <AdminDashboardShed setActiveTab={setActiveTab} />;
      case 'dealer-dashboard':
        return <DealerDashboard setActiveTab={setActiveTab} />;
      case 'dealers':
        return <MasterDealerManager onNavigate={setActiveTab} />;
      case 'sheds':
        return <ShedManager />;
      case 'colors':
        return <DealerColorManager />;
      case 'sizes':
        return <DealerSizeManager />;
      case 'windows':
        return <DealerWindowSizeManager />;
      case 'leads':
        return <LeadManagement />;
      case 'payments':
        return <PaymentManagement />;
      case 'analytics':
        return <AdminAnalytics />;
      case 'settings':
        return <AdminSettings />;
      default:
        return <AdminDashboardShed setActiveTab={setActiveTab} />;
    }
  };

  return (
    <AuthGuard>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="min-h-screen bg-gray-50 dark-mode:bg-slate-900 transition-colors duration-300"
      >
        <Helmet>
          <title>Admin Dashboard - ShedX</title>
          <meta name="robots" content="noindex, nofollow" />
        </Helmet>

        <AdminNavigationShed activeTab={activeTab} setActiveTab={setActiveTab} userRole={userRole || 'master_admin'} isSidebarOpen={isSidebarOpen} setIsSidebarOpen={setIsSidebarOpen} />
        
        {/* Add padding-top to account for fixed header */}
        <div className={`pt-16 transition-all duration-300 ${isSidebarOpen ? 'lg:ml-64' : 'lg:ml-20'}`}>
          <main className="flex-1 p-4 sm:p-6">
            {/* Email configuration warning */}
            <EmailStatusChecker />
            {renderContent()}
          </main>
        </div>
      </motion.div>
    </AuthGuard>
  );
};

export default Admin;